import clsx from "clsx";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import HandLeft from "@/assets/friendsWithBenefits/handLeft.svg";
import HandRight from "@/assets/friendsWithBenefits/handRight.svg";
import Logo from "@/assets/header/Logo.svg?react";
import Peace from "@/assets/header/peace.svg";
import SimRed from "@/assets/header/sim-red.svg";
import Sims from "@/assets/header/sims.svg";
import { FocusRing } from "@/components/Layout/BaseLayouts/Footer/FocusRing";
import { BurgerDrawer } from "@/components/Layout/BaseLayouts/Sidebar/BurgerDrawer/BurgerDrawer";
import { usePortalShellConfig } from "@/components/Layout/PortalShellConfig";
import { useScrollDirectionContext } from "@/provider/ScrollDirectionProvider/ScrollDirectionProvider";
import { KEYBOARD_TAB_INDEX } from "@/utils/keyboardTabIndex";

const SimsIllustration = () => (
  <>
    <img
      className="pointer-events-none absolute left-0 top-[7.5px] w-[74px] desktop:hidden"
      src={SimRed}
      alt=""
    />
    <img
      className="pointer-events-none absolute -top-4 left-[10px] hidden w-20 desktop:block desktop:w-36"
      src={Sims}
      alt=""
    />
    <div className="pointer-events-none absolute bottom-0 right-0 hidden h-[112px] w-[124px] overflow-hidden desktop:block">
      <img className="h-[124px] w-[124px] object-contain" src={Peace} alt="" />
    </div>
  </>
);

const HandsIllustration = () => (
  <>
    <img
      className="pointer-events-none absolute left-0 top-4 w-16 desktop:top-6 desktop:w-[251px]"
      src={HandRight}
      alt=""
    />
    <img
      className="pointer-events-none absolute right-0 top-0 hidden desktop:block"
      src={HandLeft}
      alt=""
    />
  </>
);

/**
 * "sims" is the standard pre-login hero, "hands" the Friends with Benefits
 * one, "neutral" the plain ivory headline block of the mandatory profile
 * steps (initial password, personal data).
 */
const heroThemeMapping = {
  sims: {
    banner: "bg-surface-accent-blue",
    headline: "text-foreground-primary-inverse",
    Illustration: SimsIllustration,
  },
  hands: {
    banner: "bg-surface-accent-mint",
    headline: "text-foreground-primary",
    Illustration: HandsIllustration,
  },
  neutral: {
    banner: "bg-surface-accent-ivory",
    headline: "text-foreground-primary",
    Illustration: null,
  },
};

export type HeroTheme = keyof typeof heroThemeMapping;

interface Props {
  variant: "welcome" | "portal";
  headline?: string;
  /** Renders the hero headline as the page's h1 - for pages without their own. */
  headlineIsPageTitle?: boolean;
  heroTheme?: HeroTheme;
  isMobile?: boolean;
}

const WelcomeLogo = () => (
  <div className="mb-4 mt-8 !h-8 pl-6 desktop:h-20 desktop:pl-8">
    <Logo />
  </div>
);

const PortalLogo = () => {
  const { t } = useTranslation();
  const { homeLink } = usePortalShellConfig();

  if (!homeLink) {
    return (
      <Logo role="img" aria-hidden={false} aria-label={t("Common.logoName")} />
    );
  }

  return (
    <Link
      className="group relative rounded-md outline-none"
      to={homeLink}
      tabIndex={KEYBOARD_TAB_INDEX}
      aria-label={t("Common.logoName")}
    >
      <FocusRing />
      <Logo />
    </Link>
  );
};

export const Header: FC<Props> = ({
  variant,
  headline,
  headlineIsPageTitle = false,
  heroTheme = "sims",
  isMobile,
}) => {
  const { t } = useTranslation();
  const { atTop } = useScrollDirectionContext();
  // Flush full-width bar at rest, floating rounded card once scrolled away
  // from the top -- same toggle as BottomNavBar's atTop-driven state.
  const floating = isMobile && !atTop;

  if (variant === "welcome") {
    const hero = heroThemeMapping[heroTheme];
    const HeroHeadline = headlineIsPageTitle ? "h1" : "p";

    return (
      <header className="flex w-full flex-col bg-background-secondary">
        <WelcomeLogo />
        <div className="relative px-4 desktop:px-6">
          <div
            className={clsx(
              "flex h-[200px] flex-col justify-center rounded-lg px-4 py-8 desktop:h-[174px]",
              hero.banner,
            )}
          >
            <HeroHeadline
              className={clsx(
                "text-center text-headline-xl font-semibold leading-xs",
                hero.headline,
              )}
            >
              {headline ?? t("Header.welcomeHeadline")}
            </HeroHeadline>
          </div>

          {hero.Illustration && <hero.Illustration />}
        </div>
      </header>
    );
  }

  return (
    <header
      data-testid="header-root"
      className={clsx(
        "flex w-full flex-col transition-transform duration-300 ease-in-out",
        // Card is 56px (h-13 in this project's custom spacing scale where
        // 13=56px/14=64px), 64px total once the 8px floating gap is added.
        isMobile && "fixed left-0 right-0 top-0 z-20",
        floating && "px-2 pt-2",
      )}
    >
      <div
        className={clsx(
          "flex items-center justify-between bg-surface-accent-ivory transition-[border-radius] duration-200 ease-in-out",
          isMobile
            ? "h-13 px-4"
            : "h-20 pl-6 pr-6 pt-6 tablet:pl-8 tablet:pr-8 tablet:pt-8",
          floating && "rounded-lg",
        )}
      >
        <PortalLogo />
        {isMobile && <BurgerDrawer floating={floating} />}
      </div>
    </header>
  );
};
